<script setup lang="ts">
import { Menu } from '@lucide/vue'
import { computed } from 'vue'
import SiteLink from './SiteLink.vue'
import type { WebsiteLink, WebsiteNavigation, WebsiteNavigationItem } from './types'

const props = withDefaults(defineProps<{
  navigation: WebsiteNavigation
  tone?: 'light' | 'dark'
}>(), {
  tone: 'light',
})

const items = computed(() => props.navigation.items.filter((item) => item.isVisible !== false))
const textClass = computed(() => props.tone === 'dark'
  ? 'site:text-(--site-color-primary-text)'
  : 'site:text-(--site-color-muted-text)')
const panelClass = computed(() => props.tone === 'dark'
  ? 'site:border-white/15 site:bg-(--site-color-text) site:text-(--site-color-primary-text)'
  : 'site:border-(--site-color-border) site:bg-(--site-color-surface) site:text-(--site-color-text)')
const triggerClass = computed(() => props.tone === 'dark'
  ? 'site:border-white/15 site:bg-white/10 site:text-(--site-color-primary-text)'
  : 'site:border-(--site-color-border) site:bg-(--site-color-background) site:text-(--site-color-text)')

function visibleChildren(item: WebsiteNavigationItem): WebsiteNavigationItem[] {
  return item.children.filter((child) => child.isVisible !== false)
}

function itemLink(item: WebsiteNavigationItem): WebsiteLink | undefined {
  return item.kind === 'link' && item.destination
    ? { label: item.label, destination: item.destination }
    : undefined
}
</script>

<template>
  <nav :aria-label="navigation.name">
    <ul class="site-navigation__desktop site:items-center site:gap-6">
      <li v-for="item in items" :key="item.id" class="site:relative">
        <SiteLink
          v-if="visibleChildren(item).length === 0 && itemLink(item)"
          :link="itemLink(item)!"
          class="site:text-sm site:font-medium"
          :class="textClass"
        >
          {{ item.label }}
        </SiteLink>

        <details v-else class="site:group site:relative">
          <summary class="site:flex site:list-none site:items-center site:gap-1.5 site:text-sm site:font-medium site:[&::-webkit-details-marker]:hidden" :class="textClass">
            {{ item.label }}
            <span class="site:text-xs site:opacity-55 site:transition-transform site:group-open:rotate-180" aria-hidden="true">⌄</span>
          </summary>
          <div class="site:absolute site:left-0 site:top-[calc(100%+0.75rem)] site:z-30 site:w-64 site:rounded-(--site-radius-md) site:border site:p-2 site:shadow-(--site-shadow-md)" :class="panelClass">
            <SiteLink v-if="itemLink(item)" :link="itemLink(item)!" class="site:block site:rounded-(--site-radius-sm) site:px-3 site:py-2.5 site:text-sm site:font-semibold">
              View {{ item.label }}
            </SiteLink>
            <div v-for="child in visibleChildren(item)" :key="child.id">
              <details v-if="visibleChildren(child).length" class="site:group/child">
                <summary class="site:flex site:list-none site:items-center site:justify-between site:rounded-(--site-radius-sm) site:px-3 site:py-2.5 site:text-sm site:font-medium site:[&::-webkit-details-marker]:hidden">
                  {{ child.label }}
                  <span class="site:text-xs site:opacity-50 site:transition-transform site:group-open/child:rotate-180" aria-hidden="true">⌄</span>
                </summary>
                <div class="site:ml-3 site:border-l site:border-(--site-color-border) site:pl-2">
                  <SiteLink v-if="itemLink(child)" :link="itemLink(child)!" class="site:block site:rounded-(--site-radius-sm) site:px-3 site:py-2 site:text-sm site:font-medium">
                    View {{ child.label }}
                  </SiteLink>
                  <template v-for="grandchild in visibleChildren(child)" :key="grandchild.id">
                    <SiteLink
                      v-if="itemLink(grandchild)"
                      :link="itemLink(grandchild)!"
                      class="site:block site:rounded-(--site-radius-sm) site:px-3 site:py-2 site:text-sm site:opacity-75"
                    >
                      {{ grandchild.label }}
                    </SiteLink>
                    <span v-else class="site:block site:px-3 site:py-2 site:text-sm site:font-semibold">{{ grandchild.label }}</span>
                  </template>
                </div>
              </details>
              <SiteLink v-else-if="itemLink(child)" :link="itemLink(child)!" class="site:block site:rounded-(--site-radius-sm) site:px-3 site:py-2.5 site:text-sm">
                {{ child.label }}
              </SiteLink>
              <span v-else class="site:block site:px-3 site:py-2.5 site:text-sm site:font-semibold">{{ child.label }}</span>
            </div>
          </div>
        </details>
      </li>
    </ul>

    <details class="site-navigation__compact site:group site:relative">
      <summary
        aria-label="Open navigation menu"
        class="site:grid site:size-11 site:list-none site:place-items-center site:rounded-(--site-radius-sm) site:border site:shadow-(--site-shadow-sm) site:transition-colors site:[&::-webkit-details-marker]:hidden"
        :class="triggerClass"
      >
        <Menu :size="19" :stroke-width="1.7" aria-hidden="true" />
      </summary>
      <div class="site:absolute site:right-0 site:top-[calc(100%+0.5rem)] site:z-30 site:max-h-[70dvh] site:w-[min(20rem,calc(100vw-2rem))] site:overflow-y-auto site:rounded-(--site-radius-md) site:border site:p-2 site:shadow-(--site-shadow-md)" :class="panelClass">
        <div v-for="item in items" :key="item.id">
          <details v-if="visibleChildren(item).length" class="site:group/mobile">
            <summary class="site:flex site:min-h-11 site:list-none site:items-center site:justify-between site:rounded-(--site-radius-sm) site:px-3 site:text-sm site:font-semibold site:[&::-webkit-details-marker]:hidden">
              {{ item.label }}
              <span class="site:text-xs site:opacity-55 site:transition-transform site:group-open/mobile:rotate-180" aria-hidden="true">⌄</span>
            </summary>
            <div class="site:ml-3 site:border-l site:border-(--site-color-border) site:pl-2">
              <SiteLink v-if="itemLink(item)" :link="itemLink(item)!" class="site:block site:px-3 site:py-2.5 site:text-sm site:font-medium">
                View {{ item.label }}
              </SiteLink>
              <template v-for="child in visibleChildren(item)" :key="child.id">
                <details v-if="visibleChildren(child).length" class="site:group/mobile-child">
                  <summary class="site:flex site:min-h-11 site:list-none site:items-center site:justify-between site:px-3 site:text-sm site:[&::-webkit-details-marker]:hidden">
                    {{ child.label }}
                    <span class="site:text-xs site:opacity-50 site:group-open/mobile-child:rotate-180" aria-hidden="true">⌄</span>
                  </summary>
                  <div class="site:ml-3 site:grid site:border-l site:border-(--site-color-border) site:pl-2">
                    <SiteLink v-if="itemLink(child)" :link="itemLink(child)!" class="site:px-3 site:py-2.5 site:text-sm">View {{ child.label }}</SiteLink>
                    <template v-for="grandchild in visibleChildren(child)" :key="grandchild.id">
                      <SiteLink v-if="itemLink(grandchild)" :link="itemLink(grandchild)!" class="site:px-3 site:py-2.5 site:text-sm site:opacity-75">
                        {{ grandchild.label }}
                      </SiteLink>
                      <span v-else class="site:px-3 site:py-2.5 site:text-sm site:font-semibold">{{ grandchild.label }}</span>
                    </template>
                  </div>
                </details>
                <SiteLink v-else-if="itemLink(child)" :link="itemLink(child)!" class="site:block site:px-3 site:py-2.5 site:text-sm">{{ child.label }}</SiteLink>
                <span v-else class="site:block site:px-3 site:py-2.5 site:text-sm site:font-semibold">{{ child.label }}</span>
              </template>
            </div>
          </details>
          <SiteLink v-else-if="itemLink(item)" :link="itemLink(item)!" class="site:block site:min-h-11 site:px-3 site:py-2.5 site:text-sm site:font-medium">
            {{ item.label }}
          </SiteLink>
          <span v-else class="site:block site:min-h-11 site:px-3 site:py-2.5 site:text-sm site:font-semibold">{{ item.label }}</span>
        </div>
      </div>
    </details>
  </nav>
</template>

<style scoped>
.site-navigation__desktop {
  display: none;
}

@container site-renderer (min-width: 64rem) {
  .site-navigation__desktop {
    display: flex;
  }

  .site-navigation__compact {
    display: none;
  }
}
</style>
